// Central content + asset references for RestPower.
// RestPower is an early-stage / in-development concept. All imagery is concept
// visualization and copy avoids unsupported operational claims.

export const IMG = {
  // Authentic RestPower concept renders (optimized WebP, served locally)
  exterior: "/renders/exterior.webp",
  lounge: "/renders/lounge.webp",
  cafe: "/renders/cafe.webp",
  market: "/renders/market.webp",
  restrooms: "/renders/restrooms.webp",
  gaming: "/renders/gaming.webp",
  // Supporting imagery
  road: "https://images.unsplash.com/photo-1668966643905-bf39ebd0577b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  developmentAerial: "https://static.prod-images.emergentagent.com/jobs/52e55279-1746-4cea-94ec-0d98c616c929/images/009e8ed740a1fc2c1f4a3d09b7a5dd39dd443137522ce7e8f183a37b692981aa.jpeg",
};

// Six-pillar concept summary ("Charge the car. Recharge yourself.")
export const PILLARS = [
  { icon: "Zap", title: "Charge", text: "Ultra-fast EV charging" },
  { icon: "Coffee", title: "Eat", text: "Fresh food and coffee" },
  { icon: "Armchair", title: "Rest", text: "Comfortable premium lounge" },
  { icon: "Laptop", title: "Work", text: "Wi-Fi, outlets and quiet workspace" },
  { icon: "Gamepad2", title: "Play", text: "Gaming and entertainment" },
  { icon: "ShoppingBag", title: "Shop", text: "Travel essentials and grab-and-go" },
];

export const FEATURES = [
  { icon: "Zap", title: "Fast Charging", text: "Designed around future DC fast-charging infrastructure for convenient travel stops." },
  { icon: "Coffee", title: "Premium Amenities", text: "Coffee, food, clean restrooms, and comfortable spaces." },
  { icon: "Armchair", title: "Relax & Stay Awhile", text: "Lounge, work, or unwind while your vehicle charges." },
  { icon: "ShoppingBag", title: "Curated Market", text: "Travel essentials, snacks, beverages, and thoughtfully selected products." },
  { icon: "Leaf", title: "Sustainable by Design", text: "A concept designed around electric mobility and responsible infrastructure." },
];

// Problem → solution narrative
export const PROBLEMS = [
  { icon: "Armchair", title: "Nowhere comfortable to wait", text: "Ten to thirty minutes with nowhere decent to sit." },
  { icon: "ShowerHead", title: "Restrooms you avoid", text: "Facilities that make a quick stop feel like a compromise." },
  { icon: "WifiOff", title: "Poor connectivity", text: "Spotty signal and no real place to get work done." },
  { icon: "Utensils", title: "Limited food", text: "Vending machines instead of an actual meal." },
  { icon: "Clock", title: "Little to do while charging", text: "Charging time that becomes dead time." },
  { icon: "MoonStar", title: "Weak nighttime experience", text: "Poorly lit, uninviting stops after dark." },
];

// Amenity showcase sections (use authentic renders)
export const AMENITIES = [
  {
    key: "charging",
    label: "Charging",
    image: IMG.exterior,
    title: "Built for the electric road ahead.",
    tagline: "Charge the car.",
    text: "RestPower is designed around high-speed DC charging infrastructure capable of supporting next-generation roadside travel — with amenities just steps from every stall.",
    points: [
      "Target charging capability: up to 350kW DC fast charging",
      "Real-time charging status, designed into the experience",
      "Well-lit, accessible charging areas",
      "Simple, contactless payment (planned)",
    ],
  },
  {
    key: "lounge",
    label: "Lounge",
    image: IMG.lounge,
    title: "Rest while your car recharges.",
    tagline: "A roadside stop that actually feels like a break.",
    text: "The heart of every RestPower location: a warm, low-lit space built around comfort. Sink into a real chair, get an hour of focused work done, or just watch the charge tick up.",
    points: [
      "Comfortable sofas and lounge chairs",
      "Warm ambient lighting and quiet zones",
      "High-speed Wi-Fi and power at every table",
      "Clean, modern restrooms just steps away",
    ],
  },
  {
    key: "cafe",
    label: "Café",
    image: IMG.cafe,
    title: "Eat well. Travel better.",
    tagline: "Fresh food and real coffee — not an afterthought.",
    text: "A café concept designed for travelers: fresh-brewed coffee, espresso drinks, and made-to-order and grab-and-go options, with seating for a real meal rather than a rushed bite.",
    points: [
      "Espresso bar and fresh-brewed coffee",
      "Breakfast, fresh meals and pastries",
      "Grab-and-go options for the road",
      "Comfortable seating and convenient ordering",
    ],
    note: "Any prices shown in concept imagery are illustrative.",
  },
  {
    key: "restrooms",
    label: "Restrooms",
    image: IMG.restrooms,
    title: "The restroom shouldn't be the part of the trip you dread.",
    tagline: "Clean. Fresh. Ready for the road ahead.",
    text: "RestPower is designed to make one of the most overlooked parts of roadside travel clean, comfortable, and dependable — a genuine differentiator.",
    points: [
      "Modern finishes and bright, thoughtful lighting",
      "Private stalls and touchless fixtures (planned)",
      "Accessible facilities and baby-changing accommodations",
      "Designed for routine cleaning and upkeep",
    ],
  },
  {
    key: "market",
    label: "Market",
    image: IMG.market,
    title: "Everything you forgot. Everything you need.",
    tagline: "Travel essentials, done right.",
    text: "A curated market concept with the snacks, drinks, and road-trip necessities travelers actually reach for — thoughtfully merchandised, always in stock.",
    points: [
      "Snacks, drinks and grab-and-go meals",
      "Charging cables and travel accessories",
      "Personal-care products and necessities",
      "Convenience items for the road ahead",
    ],
    note: "Products shown in concept imagery are illustrative and do not imply brand relationships.",
  },
  {
    key: "gaming",
    label: "Gaming",
    image: IMG.gaming,
    title: "Waiting just became playtime.",
    tagline: "Play. Compete. Connect.",
    text: "A social space designed to make charging time enjoyable — especially for families and groups traveling together. Premium in feel, never a children's arcade.",
    points: [
      "Arcade cabinets, consoles and a big screen",
      "Multiplayer and family-friendly entertainment",
      "Comfortable social seating",
      "Something enjoyable to do while you charge",
    ],
  },
];

export const EXPERIENCES = [
  { key: "power-up", num: "01", title: "Power Up", text: "Fast, convenient EV charging designed to get travelers back on the road.", image: IMG.exterior, concept: true },
  { key: "fuel-up", num: "02", title: "Fuel Up", text: "Quality coffee, food, beverages, and grab-and-go options.", image: IMG.cafe, concept: true },
  { key: "reset", num: "03", title: "Reset", text: "Comfortable lounge and workspace environments designed for charging downtime.", image: IMG.lounge, concept: true },
  { key: "gear-up", num: "04", title: "Gear Up", text: "Travel essentials and thoughtfully selected products for the road ahead.", image: IMG.market, concept: true },
];

// Illustrative 20-minute stop
export const TIMELINE = [
  { t: "0 MIN", title: "Plug in", text: "Arrive and start your charge." },
  { t: "3 MIN", title: "Grab coffee or food", text: "Order at the café on your way in." },
  { t: "5 MIN", title: "Settle into the lounge", text: "Find a comfortable seat and connect to Wi-Fi." },
  { t: "10 MIN", title: "Work, shop, or play", text: "Answer emails, browse the market, or unwind." },
  { t: "20 MIN", title: "Continue your trip", text: "Check your vehicle and get back on the road, refreshed." },
];

// Neutral comparison
export const COMPARISON = [
  { row: "Charging experience", rp: "Designed around high-speed DC charging", trad: "Speeds vary by location" },
  { row: "Waiting environment", rp: "Lounge, café, market and workspace", trad: "Amenities vary by location" },
  { row: "Food", rp: "Fresh food and coffee, on site", trad: "Often limited or vending only" },
  { row: "Restrooms", rp: "Clean, modern, well-maintained (planned)", trad: "Varies widely" },
  { row: "Connectivity", rp: "Free high-speed Wi-Fi (planned)", trad: "Inconsistent" },
  { row: "Workspace", rp: "Quiet zones with power at every table", trad: "Rarely available" },
  { row: "Entertainment", rp: "Gaming and social spaces", trad: "Typically none" },
  { row: "Atmosphere", rp: "Premium, well-lit, welcoming", trad: "Varies by location" },
];

// Personas (NOT testimonials)
export const PERSONAS = [
  { title: "The Road-Trip Family", text: "Food, restrooms, entertainment and room to reset — so everyone arrives happier.", icon: "Users" },
  { title: "The Remote Professional", text: "Wi-Fi, charging outlets, coffee and a quiet workspace to stay productive.", icon: "Laptop" },
  { title: "The Long-Distance EV Driver", text: "Reliable charging paired with genuinely useful amenities.", icon: "Zap" },
  { title: "The Rideshare & Delivery Driver", text: "A safe place to recharge both the vehicle and the driver.", icon: "Car" },
  { title: "The Everyday Traveler", text: "Coffee, food, clean facilities and essentials in one convenient stop.", icon: "Coffee" },
];

export const PARTNER_TYPES = [
  "Property Owners", "Developers", "Charging Infrastructure", "Utilities",
  "Municipalities", "Retail & Food", "Fleet Partners", "Investors",
];

export const INQUIRY_OPTIONS = [
  { value: "real-estate", label: "Real Estate / Site Opportunities" },
  { value: "ev-infrastructure", label: "EV Infrastructure" },
  { value: "utilities-energy", label: "Utilities / Energy" },
  { value: "hospitality-fb", label: "Hospitality / Food & Beverage" },
  { value: "fleet", label: "Fleet Opportunities" },
  { value: "brand", label: "Brand Partnerships" },
  { value: "investment", label: "Investment Inquiries" },
  { value: "general", label: "General Business Inquiries" },
];

export const FAQS = [
  { q: "What is RestPower?", a: "RestPower is a concept for a next-generation roadside destination that combines high-speed EV charging with a premium lounge, café, market, clean restrooms, workspace, and entertainment — designed around both the vehicle and the person waiting for it." },
  { q: "Is RestPower currently open?", a: "No. RestPower is an early-stage company in active development. We're building the foundation for our first destination and do not yet have operating locations." },
  { q: "What amenities are planned?", a: "Planned amenities include ultra-fast charging, a comfortable lounge, café and fresh food, a curated market, clean modern restrooms, Wi-Fi and workspace, and gaming and entertainment." },
  { q: "What types of EV charging will RestPower support?", a: "RestPower is being designed around high-speed DC fast charging with a target capability of up to 350kW. Specific connector support and hardware will be confirmed as the concept develops." },
  { q: "How long does EV charging take?", a: "Charging time depends on your vehicle, battery, the charger's capability, temperature, and state of charge. Our goal is to make whatever time you spend genuinely useful and comfortable." },
  { q: "Will the lounge be available to all travelers?", a: "Access details will be finalized as we develop the concept. Our intent is to make RestPower a welcoming destination for travelers." },
  { q: "Where will RestPower locations be built?", a: "Locations will be announced as sites are confirmed. Join our list to be notified as our plans progress." },
  { q: "Can property owners or developers partner with RestPower?", a: "Yes. We're exploring opportunities with property owners and developers — reach out through our partnership inquiry form." },
  { q: "Can charging companies or infrastructure providers work with RestPower?", a: "Yes. We welcome conversations with charging and infrastructure partners through our partnership inquiry form." },
];

export const JOURNAL_POSTS = [
  {
    slug: "reimagining-the-charging-stop",
    title: "Reimagining the Charging Stop",
    category: "Vision",
    date: "Concept Series",
    readTime: "4 min read",
    image: IMG.exterior,
    concept: true,
    excerpt: "Why the future of EV charging isn't just about kilowatts — it's about the experience of the people waiting.",
    body: [
      { type: "p", text: "For most of the last century, the roadside stop has served a single purpose: refuel the vehicle and get back on the road as quickly as possible. It was designed around the machine, not the person." },
      { type: "p", text: "Electric mobility changes the math. Charging takes longer than filling a tank, which means travelers now have time on their hands at every stop. That time is either wasted in a parking lot — or it becomes an opportunity." },
      { type: "h", text: "Designing for the person, not just the vehicle" },
      { type: "p", text: "RestPower is being designed around a simple belief: if people are going to spend time at a charging stop, that time should be comfortable, useful, and even enjoyable. A great coffee. A quiet place to work. A comfortable seat. A curated market for the road ahead." },
      { type: "p", text: "This is a concept article that reflects our early thinking as we develop the RestPower experience. Nothing here describes an existing location." },
    ],
  },
  {
    slug: "what-makes-a-destination",
    title: "What Makes a Stop a Destination",
    category: "Design",
    date: "Concept Series",
    readTime: "5 min read",
    image: IMG.lounge,
    concept: true,
    excerpt: "The difference between somewhere you have to stop and somewhere you want to stop is design.",
    body: [
      { type: "p", text: "A destination is a place people choose, not just a place they tolerate. The best hospitality brands understand that the details — light, materials, sound, comfort, and flow — are what turn a transaction into an experience." },
      { type: "h", text: "Hospitality meets infrastructure" },
      { type: "p", text: "RestPower brings that same intentionality to clean-energy infrastructure. Warm interior lighting, thoughtful architecture, comfortable spaces, and quality amenities — combined with reliable, high-speed charging." },
      { type: "p", text: "These are concept explorations shared as part of our development journey. Any imagery is a concept visualization, not a photograph of a real facility." },
    ],
  },
  {
    slug: "building-a-better-stop",
    title: "Building a Better Stop",
    category: "Journey",
    date: "Concept Series",
    readTime: "3 min read",
    image: IMG.market,
    concept: true,
    excerpt: "An honest look at where RestPower is today — early, ambitious, and just getting started.",
    body: [
      { type: "p", text: "We believe in being transparent about where we are. RestPower is an early-stage company. We don't have operating locations yet. We're building the foundation for our first destination and exploring the partnerships, locations, and infrastructure required to make it real." },
      { type: "h", text: "The road ahead" },
      { type: "p", text: "Great infrastructure takes time, the right partners, and a clear vision. We're focused on getting the concept and the fundamentals right — and sharing the journey along the way." },
      { type: "p", text: "This is a concept article reflecting our current thinking. It does not describe completed developments or signed deals." },
    ],
  },
];

export const STORY_CHAPTERS = [
  { num: "01", title: "The Idea", text: "RestPower began with a simple observation: charging an electric vehicle takes time, and that time is almost always spent waiting. We asked a better question — what if that time could be useful, comfortable, and even enjoyable?" },
  { num: "02", title: "The Insight", text: "The idea was shaped by firsthand experience driving rideshare full-time in rented electric vehicles. Long charging stops created a recurring problem: choosing between charging the car and taking care of basic needs — a restroom, a meal, a comfortable place to wait." },
  { num: "03", title: "The Vision", text: "That gap led to a conviction: charging destinations should be built around both the vehicle and the person driving it. High-speed charging paired with premium hospitality, comfortable spaces, quality food, and a curated market." },
  { num: "04", title: "The Journey", text: "We're at the very beginning. RestPower is in active development as we build the foundation for our first destination and explore the partnerships, locations, and infrastructure required to bring this experience to life." },
];
